/* eslint-disable react-hooks/exhaustive-deps */
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Avatar,
  Box,
  Button,
  Flex,
  Heading,
  Spinner,
  Text,
  VStack
} from "@chakra-ui/react";
import React, {useEffect, useState} from "react";
import {Link as RouterLink, useNavigate} from "react-router-dom";
import {observer} from "mobx-react-lite";
import {Card} from "../components/Card";
import {useAPI} from "../hooks/useAPI";
import {BookingGroupResponse, WishlistItemResponse} from "../services/responses";
import {toastMessage} from "../services/notify";
import {useToast} from "@chakra-ui/react";

const MyBookingsPage = () => {
  const api = useAPI();
  const navigate = useNavigate();
  const toast = useToast();

  const [loaded, setLoaded] = useState(false);
  const [groups, setGroups] = useState<BookingGroupResponse[]>([]);
  const [busyItem, setBusyItem] = useState<string | null>(null);
  const [confirmItem, setConfirmItem] = useState<WishlistItemResponse | null>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  useEffect(() => {
    api.checkAuthorization()
      .then((loggedIn) => {
        if (!loggedIn) {
          navigate("/signin");
          return [];
        }
        return api.getMyBookings();
      })
      .then((response) => {
        setGroups(response);
        setLoaded(true);
      });
  }, []);

  const unbook = (item: WishlistItemResponse) => {
    setBusyItem(item.uuid);
    api.unbookItem(item.uuid)
      .then((response) => {
        if (response === null) {
          toast(toastMessage('Could not cancel the booking.', 'error'));
          return;
        }
        // drop the item, and the whole group once it becomes empty
        setGroups((current) => current
          .map((group) => ({...group, items: group.items.filter((i) => i.uuid !== item.uuid)}))
          .filter((group) => group.items.length > 0));
        toast(toastMessage('Booking cancelled.', 'success'));
      })
      .finally(() => setBusyItem(null));
  };

  if (!loaded) {
    return <Flex justifyContent="center" alignItems="center" flexGrow={1} mt="3rem">
      <Spinner size="xl"/>
    </Flex>;
  }

  return <>
    <Heading size={{base: "md", sm: "lg"}} mb={5}>My bookings</Heading>

    {!groups.length && (
      <Box textAlign="center" mt="3rem">
        <VStack color="gray.500" fontSize={{base: "xs", md: "sm"}} gap={0}>
          <Text fontSize={{base: "36px", md: "48px"}}>¯\_(ツ)_/¯</Text>
          <Text>You have not booked anything yet.</Text>
          <Text>Open a friend's wishlist and reserve a gift.</Text>
        </VStack>
      </Box>
    )}

    <VStack align="stretch" spacing={6}>
      {groups.map((group) => (
        <Box key={`${group.list_uuid ?? 'profile'}-${group.owner.username}`} bg="white" p={4} borderRadius="lg">
          <Flex alignItems="center" gap={3} mb={3} flexWrap="wrap">
            <Avatar size="sm" bg="teal.500" name={(group.owner.name || group.owner.username).substring(0, 1)}
                    src={group.owner.userpic || ''}/>
            <Heading as="h2" size="sm">
              {group.list_name && group.list_uuid && (
                <>
                  <Text as={RouterLink} to={`/${group.owner.username}/${group.list_uuid}`} color="brand.500">
                    {group.list_name}
                  </Text>
                  <Text as="span" color="gray.500" fontWeight="normal"> • </Text>
                </>
              )}
              <Text as={RouterLink} to={`/${group.owner.username}`} color="brand.500"
                    fontWeight={group.list_name ? "normal" : "bold"}>
                {group.owner.name || group.owner.username}
              </Text>
            </Heading>
          </Flex>

          <Flex justifyContent="space-between" flexWrap="wrap" gap={3}>
            {group.items.map((item) => (
              <Card
                key={item.uuid}
                uuid={item.uuid}
                image={item.image || ''}
                title={item.name}
                description={item.description ?? undefined}
                link={item.link}
                hasHandle={false}
                editable={false}
                booked={true}
                bookedByMe={true}
                canUnbook={true}
                bookingBusy={busyItem === item.uuid}
                onUnbook={() => setConfirmItem(item)}
              />
            ))}
          </Flex>
        </Box>
      ))}
    </VStack>

    <AlertDialog motionPreset="slideInBottom" isOpen={confirmItem !== null} isCentered
                 onClose={() => setConfirmItem(null)} leastDestructiveRef={cancelRef}>
      <AlertDialogOverlay/>
      <AlertDialogContent>
        <AlertDialogHeader>Cancel booking?</AlertDialogHeader>
        <AlertDialogCloseButton/>
        <AlertDialogBody>
          Are you sure you want to cancel your booking of <b>{confirmItem?.name}</b>? Someone else will be able to book
          it.
        </AlertDialogBody>
        <AlertDialogFooter>
          <Button ref={cancelRef} onClick={() => setConfirmItem(null)}>Keep it</Button>
          <Button colorScheme="red" ml={3} onClick={() => {
            const item = confirmItem;
            setConfirmItem(null);
            if (item) {
              unbook(item);
            }
          }}>Cancel booking</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>;
};

export default observer(MyBookingsPage);
